import { matchPath } from 'react-router-dom';
import type { ModuleDefinition, ModuleInfo, ModuleRoute, NavigationItem, RouteLayout } from './types';

const modules: ModuleDefinition[] = [];

export const registeredModules = modules;

export const registerModule = (module: ModuleDefinition) => {
  const exists = modules.some((m) => m.name === module.name);
  if (!exists) {
    modules.push(module);
  }
};

export const getModule = (name: string) => modules.find((module) => module.name === name);

export const getNavigation = (): NavigationItem[] =>
  modules.flatMap((module) => module.navigation ?? []);

/**
 * Get navigation items for a specific module
 */
export const getModuleNavigation = (moduleName: string): NavigationItem[] => {
  const module = getModule(moduleName);
  return module?.navigation ?? [];
};

/**
 * Get all module info for the module switcher
 * Returns modules sorted by their order
 */
export const getModuleInfoList = (): ModuleInfo[] =>
  modules
    .filter((m) => m.moduleInfo)
    .map((m) => m.moduleInfo!)
    .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));

/**
 * Get module by its base path
 */
export const getModuleByPath = (path: string): ModuleDefinition | undefined => {
  // Normalize the path to match the basePath format
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return modules.find((m) => normalizedPath.startsWith(m.basePath));
};

/**
 * Whether a module route renders the given path (e.g. "/korisnici/123/izmeni" -> true,
 * "/korisnici/123" -> false when the module has no detail route).
 * Used by the breadcrumb to link only to pages that exist.
 */
export const hasModuleRoute = (path: string): boolean =>
  modules.some((module) =>
    module.routes.some((route) => {
      const pattern = route.index || !route.path ? module.basePath : `${module.basePath}/${route.path}`;
      return matchPath({ path: pattern, end: true }, path) !== null;
    })
  );

/** The module route that renders the given path, if any */
const findModuleRoute = (path: string): ModuleRoute | undefined => {
  for (const module of modules) {
    for (const route of module.routes) {
      const pattern = route.index || !route.path ? module.basePath : `${module.basePath}/${route.path}`;
      if (matchPath({ path: pattern, end: true }, path)) return route;
    }
  }
  return undefined;
};

/**
 * Shell options of the module route that renders the given path: its layout ("default"
 * when none) and whether a breadcrumb is shown (only when the route asks for it).
 */
export const getRouteShellOptions = (path: string): { layout: RouteLayout; breadcrumb: boolean } => {
  const route = findModuleRoute(path);
  // Module pages draw their own title and cards; pages outside modules keep the app card
  const layout = route ? (route.layout ?? 'centered') : 'default';
  return { layout, breadcrumb: route?.breadcrumb ?? false };
};

/**
 * Sidebar label of the navigation item for the given path, used by the breadcrumb.
 * Falls back to the group whose children live under the path (e.g. "/ljudski-resursi/katalozi").
 */
export const getNavigationLabelForPath = (path: string): Pick<NavigationItem, 'label' | 'labelKey'> | undefined => {
  const flatten = (items: NavigationItem[]): NavigationItem[] =>
    items.flatMap((item) => [item, ...flatten(item.children ?? [])]);
  const items = flatten(getNavigation());

  const exact = items.find((item) => item.path === path);
  if (exact) return exact;

  return items.find((item) => item.children?.some((child) => child.path?.startsWith(`${path}/`)));
};
